import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from "react";
import {
    isAuthenticated,
    getStoredUser,
    getCurrentUser,
    logout as authLogout,
} from "@/services/authService";
import {
    getWalletBalance,
    checkGatewayDepositStatus,
} from "@/services/walletService";
import {
    getNotifications,
    markAsRead as apiMarkAsRead,
    markAllAsRead as apiMarkAllAsRead,
} from "@/services/notificationsService";
import { showToast } from "@/components/shared/UXComponents";
import AnnouncementDialog from "@/components/AnnouncementDialog";

const AppContext = createContext(null);

// Default user for non-authenticated state — Level-native (no tier residue).
const defaultUser = {
    id: null,
    username: "Guest",
    referralCode: null,
    usernameEditable: true,
};

// Default Level data (used until /api/levels/me resolves).
const defaultLevelData = {
    level: null,
    xp_total: 0,
    xp_to_next: 0,
    progress_percent: 0,
    next_level: null,
    source: "default",
};

// Default wallet
const defaultWallet = {
    balance: 0,
    pending: 0,
    available: 0,
};

// Local storage keys for pending gateway deposit
const PENDING_GATEWAY_KEYS = {
    DEPOSIT_ID: "pending_gateway_deposit_id",
    STARTED_AT: "pending_gateway_started_at",
    MAX_MINUTES: "pending_gateway_max_minutes",
    POLL_INTERVAL: "pending_gateway_poll_interval",
    TIMEOUT_POLL_INTERVAL: "pending_gateway_timeout_poll_interval",
    ORIGINAL_AMOUNT: "pending_gateway_original_amount",
    CHARGED_AMOUNT: "pending_gateway_charged_amount",
    POLL_COUNT: "pending_gateway_poll_count",
};

// Polling schedule constants: 15s for first 12 checks, then 60s
const POLL_SCHEDULE = {
    INITIAL_INTERVAL_MS: 15000, // 15 seconds for first 12 checks
    SLOW_INTERVAL_MS: 60000, // 60 seconds after first 12 checks
    INITIAL_CHECK_COUNT: 12, // Number of checks at fast interval
};

export function AppProvider({ children }) {
    const cachedUserRef = useRef(getStoredUser());
    const [user, setUser] = useState(() => cachedUserRef.current || defaultUser);
    const [wallet, setWallet] = useState(defaultWallet);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(
        () => !(isAuthenticated() && cachedUserRef.current),
    );
    const [isLoggedIn, setIsLoggedIn] = useState(() => isAuthenticated());
    const [authStatus, setAuthStatus] = useState(() =>
        isAuthenticated() && cachedUserRef.current ? "ready" : "checking",
    );

    // Level-native shared progression state. Source of truth for current Level.
    // Shape mirrors /api/levels/me (get_progress).
    const [levelData, setLevelData] = useState(defaultLevelData);

    const refreshLevel = useCallback(async () => {
        if (!isAuthenticated()) {
            setLevelData(defaultLevelData);
            return null;
        }
        try {
            const { getMyLevel } = await import("@/services/levelsService");
            const data = await getMyLevel();
            if (data) setLevelData(data);
            return data;
        } catch {
            return null;
        }
    }, []);

    // Background gateway polling state
    const [pendingGatewayDeposit, setPendingGatewayDeposit] = useState(null);
    const backgroundPollerRef = useRef(null);
    const pollCountRef = useRef(0); // Track polling attempts for schedule

    // Load user data on mount. A slow or interrupted request must never turn a
    // valid cached session into a guest session. We clear auth only after the
    // server explicitly rejects the token.
    useEffect(() => {
        let cancelled = false;
        let retryTimer = null;
        let retryAttempt = 0;

        const scheduleRetry = (loadUserData) => {
            if (cancelled || retryTimer) return;
            const delay = Math.min(30000, 3000 * 2 ** retryAttempt);
            retryAttempt += 1;
            retryTimer = window.setTimeout(() => {
                retryTimer = null;
                loadUserData();
            }, delay);
        };

        const clearRejectedSession = () => {
            localStorage.removeItem("sessionId");
            localStorage.removeItem("user");
            setUser(defaultUser);
            setWallet(defaultWallet);
            setIsLoggedIn(false);
            setAuthStatus("signed_out");
        };

        const loadUserData = async () => {
            if (isAuthenticated()) {
                try {
                    if (!cachedUserRef.current) setAuthStatus("checking");
                    const userData = await getCurrentUser();
                    if (cancelled) return;

                    if (userData?.id) {
                        const normalizedUser = {
                            ...defaultUser,
                            ...userData,
                            usernameEditable:
                                userData.usernameEditable !== false,
                            // FIX Issue 4: Explicitly map hasLoadedOnce for first-load bonus display
                            hasLoadedOnce:
                                userData.hasLoadedOnce ||
                                userData.has_loaded_once ||
                                false,
                            // Phone verification status
                            phoneVerified:
                                userData.phoneVerified ||
                                userData.phone_verified ||
                                false,
                            phone_verified:
                                userData.phoneVerified ||
                                userData.phone_verified ||
                                false,
                        };
                        cachedUserRef.current = normalizedUser;
                        localStorage.setItem(
                            "user",
                            JSON.stringify(normalizedUser),
                        );
                        setUser(normalizedUser);
                        setIsLoggedIn(true);
                        setAuthStatus("ready");
                        retryAttempt = 0;

                        // Load wallet and notifications in parallel (non-critical)
                        const [walletData, notifData] = await Promise.all([
                            getWalletBalance().catch(() => defaultWallet),
                            getNotifications().catch(() => ({
                                notifications: [],
                                unread_count: 0,
                            })),
                        ]);

                        setWallet(walletData);

                        if (notifData.notifications) {
                            setNotifications(
                                notifData.notifications.map((n) => ({
                                    id: n.id,
                                    title: n.title,
                                    message: n.message,
                                    type: n.type || "info",
                                    category: n.category,
                                    actionUrl: n.action_url,
                                    metadata: n.metadata,
                                    read: n.read || false,
                                    createdAt: n.created_at,
                                })),
                            );
                        }

                        // Check for pending gateway deposit in localStorage
                        loadPendingGatewayDeposit();
                    } else {
                        // An empty response is not proof that the token is
                        // invalid. Keep the cached identity and retry.
                        setIsLoggedIn(true);
                        setAuthStatus("reconnecting");
                        scheduleRetry(loadUserData);
                    }
                } catch (e) {
                    if (cancelled) return;
                    const status = Number(
                        e?.response?.status || e?.status || 0,
                    );
                    console.warn(
                        "Failed to refresh user session:",
                        e,
                    );
                    if (status === 401 || status === 403) {
                        clearRejectedSession();
                    } else {
                        // Network failures, timeouts and 5xx responses retain
                        // the last valid identity and session.
                        setIsLoggedIn(true);
                        setAuthStatus("reconnecting");
                        scheduleRetry(loadUserData);
                    }
                }
            } else {
                setIsLoggedIn(false);
                setAuthStatus("signed_out");
            }
            setLoading(false);
        };

        loadUserData();

        const retryNow = () => {
            if (!isAuthenticated()) return;
            if (retryTimer) {
                window.clearTimeout(retryTimer);
                retryTimer = null;
            }
            loadUserData();
        };
        window.addEventListener("online", retryNow);
        window.addEventListener("focus", retryNow);
        window.addEventListener("mg-retry-connection", retryNow);
        return () => {
            cancelled = true;
            if (retryTimer) window.clearTimeout(retryTimer);
            window.removeEventListener("online", retryNow);
            window.removeEventListener("focus", retryNow);
            window.removeEventListener("mg-retry-connection", retryNow);
        };
    }, []);

    // Load pending gateway deposit from localStorage
    const loadPendingGatewayDeposit = useCallback(() => {
        const depositId = localStorage.getItem(PENDING_GATEWAY_KEYS.DEPOSIT_ID);
        const startedAt = localStorage.getItem(PENDING_GATEWAY_KEYS.STARTED_AT);
        const maxMinutes = localStorage.getItem(
            PENDING_GATEWAY_KEYS.MAX_MINUTES,
        );
        const savedPollCount = localStorage.getItem(
            PENDING_GATEWAY_KEYS.POLL_COUNT,
        );

        if (depositId && startedAt) {
            const started = parseInt(startedAt, 10);
            const max = parseInt(maxMinutes, 10) || 15;
            const elapsed = (Date.now() - started) / 1000 / 60; // minutes

            if (elapsed < max) {
                // Restore poll count from localStorage
                pollCountRef.current = parseInt(savedPollCount, 10) || 0;

                // Still within polling window
                setPendingGatewayDeposit({
                    depositId,
                    startedAt: started,
                    maxMinutes: max,
                    pollInterval:
                        parseInt(
                            localStorage.getItem(
                                PENDING_GATEWAY_KEYS.POLL_INTERVAL,
                            ),
                            10,
                        ) || 5,
                    timeoutPollInterval:
                        parseInt(
                            localStorage.getItem(
                                PENDING_GATEWAY_KEYS.TIMEOUT_POLL_INTERVAL,
                            ),
                            10,
                        ) || 30,
                    originalAmount:
                        parseFloat(
                            localStorage.getItem(
                                PENDING_GATEWAY_KEYS.ORIGINAL_AMOUNT,
                            ),
                        ) || 0,
                    chargedAmount:
                        parseFloat(
                            localStorage.getItem(
                                PENDING_GATEWAY_KEYS.CHARGED_AMOUNT,
                            ),
                        ) || 0,
                });
            } else {
                // Expired - clear localStorage but don't delete the deposit
                clearPendingGatewayDeposit(false);
            }
        }
    }, []);

    // Save pending gateway deposit to localStorage
    const savePendingGatewayDeposit = useCallback((deposit) => {
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.DEPOSIT_ID,
            deposit.depositId,
        );
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.STARTED_AT,
            deposit.startedAt.toString(),
        );
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.MAX_MINUTES,
            (deposit.maxMinutes || 15).toString(),
        );
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.POLL_INTERVAL,
            (deposit.pollInterval || 5).toString(),
        );
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.TIMEOUT_POLL_INTERVAL,
            (deposit.timeoutPollInterval || 30).toString(),
        );
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.ORIGINAL_AMOUNT,
            (deposit.originalAmount || 0).toString(),
        );
        localStorage.setItem(
            PENDING_GATEWAY_KEYS.CHARGED_AMOUNT,
            (deposit.chargedAmount || 0).toString(),
        );

        setPendingGatewayDeposit(deposit);
    }, []);

    // Clear pending gateway deposit from localStorage
    const clearPendingGatewayDeposit = useCallback(
        (showExpiredToast = false) => {
            Object.values(PENDING_GATEWAY_KEYS).forEach((key) =>
                localStorage.removeItem(key),
            );
            setPendingGatewayDeposit(null);
            pollCountRef.current = 0;

            if (showExpiredToast) {
                showToast.info(
                    "Payment Pending",
                    "Payment still pending. You can check status from Wallet.",
                );
            }
        },
        [],
    );

    // Define refreshWallet BEFORE the polling effect that uses it
    const refreshWallet = useCallback(async () => {
        try {
            const data = await getWalletBalance();
            setWallet(data);
        } catch (e) {
            console.warn("Failed to refresh wallet");
        }
    }, []);

    // Background polling for gateway deposit
    // Schedule: 15s for first 12 checks, then 60s after that
    useEffect(() => {
        if (!pendingGatewayDeposit || !isLoggedIn) {
            if (backgroundPollerRef.current) {
                clearTimeout(backgroundPollerRef.current);
                backgroundPollerRef.current = null;
            }
            return;
        }

        const scheduleNextPoll = () => {
            // Determine interval: 15s for first 12 checks, then 60s
            const interval =
                pollCountRef.current < POLL_SCHEDULE.INITIAL_CHECK_COUNT
                    ? POLL_SCHEDULE.INITIAL_INTERVAL_MS
                    : POLL_SCHEDULE.SLOW_INTERVAL_MS;

            backgroundPollerRef.current = setTimeout(
                pollGatewayStatus,
                interval,
            );
        };

        const pollGatewayStatus = async () => {
            const { depositId, startedAt, maxMinutes } = pendingGatewayDeposit;
            const elapsed = (Date.now() - startedAt) / 1000 / 60; // minutes

            // Check if expired
            if (elapsed >= maxMinutes) {
                if (backgroundPollerRef.current) {
                    clearTimeout(backgroundPollerRef.current);
                    backgroundPollerRef.current = null;
                }
                clearPendingGatewayDeposit(true);
                return;
            }

            // Increment poll count and persist it
            pollCountRef.current += 1;
            localStorage.setItem(
                PENDING_GATEWAY_KEYS.POLL_COUNT,
                pollCountRef.current.toString(),
            );

            try {
                const response = await checkGatewayDepositStatus(depositId);
                const data = response?.data || response;

                if (data.status === "APPROVED") {
                    // Success! Refresh wallet and clear pending
                    await refreshWallet();
                    clearPendingGatewayDeposit(false);
                    showToast.success(
                        "Deposit Complete!",
                        `$${
                            data.charged_amount || data.amount
                        } has been credited to your wallet`,
                    );

                    if (backgroundPollerRef.current) {
                        clearTimeout(backgroundPollerRef.current);
                        backgroundPollerRef.current = null;
                    }
                    return; // Don't schedule next poll
                } else if (data.status === "FAILED") {
                    // Failed - clear pending
                    clearPendingGatewayDeposit(false);
                    showToast.error(
                        "Deposit Failed",
                        "Your payment could not be processed.",
                    );

                    if (backgroundPollerRef.current) {
                        clearTimeout(backgroundPollerRef.current);
                        backgroundPollerRef.current = null;
                    }
                    return; // Don't schedule next poll
                }
                // If still PENDING, schedule next poll
                scheduleNextPoll();
            } catch (error) {
                console.warn("Background gateway poll failed:", error);
                // Continue polling on error
                scheduleNextPoll();
            }
        };

        // Start polling immediately, then schedule based on count
        pollGatewayStatus();

        return () => {
            if (backgroundPollerRef.current) {
                clearTimeout(backgroundPollerRef.current);
                backgroundPollerRef.current = null;
            }
        };
    }, [
        pendingGatewayDeposit,
        isLoggedIn,
        clearPendingGatewayDeposit,
        refreshWallet,
    ]);

    const refreshNotifications = useCallback(async () => {
        try {
            const data = await getNotifications();
            if (data.notifications) {
                setNotifications(
                    data.notifications.map((n) => ({
                        id: n.id,
                        title: n.title,
                        message: n.message,
                        type: n.type || "info",
                        category: n.category,
                        actionUrl: n.action_url,
                        metadata: n.metadata,
                        read: n.read || false,
                        createdAt: n.created_at,
                    })),
                );
            }
        } catch (e) {
            console.warn("Failed to refresh notifications");
        }
    }, []);

    // Auto-refresh notifications every 30 seconds when logged in
    useEffect(() => {
        if (!isLoggedIn) return;

        // Initial refresh
        refreshNotifications();

        // Set up polling interval
        const intervalId = setInterval(() => {
            refreshNotifications();
        }, 30000); // 30 seconds

        return () => clearInterval(intervalId);
    }, [isLoggedIn, refreshNotifications]);

    // Daily login XP — idempotent per-day on server side.
    // We call it at most once per calendar day per session via a local flag.
    // Also kicks off an initial Level refresh so shared state is current.
    useEffect(() => {
        if (!isLoggedIn) return;
        // Initial Level fetch (shared across pages via useApp().levelData)
        refreshLevel();

        // One-time welcome toast for users who JUST signed up. The
        // backend already awarded the +5 DAILY_LOGIN XP at signup, so
        // we don't re-award — just surface the celebration so the new
        // player feels the leaderboard bonus the moment they land on
        // home.
        try {
            const pending = localStorage.getItem("welcome_xp_toast_pending");
            if (pending === "1") {
                localStorage.removeItem("welcome_xp_toast_pending");
                (async () => {
                    try {
                        const { showToast } =
                            await import("@/components/shared/UXComponents");
                        showToast.success?.(
                            "+5 XP",
                            "Welcome to the leaderboard! 🏆",
                        );
                    } catch {
                        /* ignore */
                    }
                })();
                // Welcome users have already received DAILY_LOGIN at
                // signup time on the server — pre-set the per-day flag
                // so the post-login claim path below stays a no-op.
                const today = new Date().toISOString().slice(0, 10);
                localStorage.setItem(`daily_login_xp_claimed_${today}`, "1");
            }
        } catch {
            /* ignore */
        }

        const today = new Date().toISOString().slice(0, 10);
        const flagKey = `daily_login_xp_claimed_${today}`;
        if (localStorage.getItem(flagKey)) return;
        (async () => {
            try {
                const { claimDailyLoginXp } =
                    await import("@/services/levelsService");
                const res = await claimDailyLoginXp();
                localStorage.setItem(flagKey, "1");
                if (res?.awarded && res?.xp) {
                    // Lightweight toast — import lazily to avoid cycle issues
                    try {
                        const { showToast } =
                            await import("@/components/shared/UXComponents");
                        showToast.info?.(`+${res.xp} XP`, "Daily login bonus");
                    } catch {
                        /* ignore */
                    }
                    // Refresh Level shared state in case XP pushed user to a new Level
                    refreshLevel();
                }
            } catch {
                /* silent — backend may be rate limited or down */
            }
        })();
    }, [isLoggedIn, refreshLevel]);

    const logout = useCallback(async () => {
        try {
            await authLogout();
        } catch (e) {
            console.warn("Logout API call failed:", e);
        }
        // Always clear local state regardless of API call result
        localStorage.removeItem("sessionId");
        localStorage.removeItem("user");
        // Clear pending gateway deposit on logout
        clearPendingGatewayDeposit(false);
        setUser(defaultUser);
        setWallet(defaultWallet);
        setNotifications([]);
        setIsLoggedIn(false);
    }, [clearPendingGatewayDeposit]);

    const unreadCount = notifications.filter((n) => !n.read).length;

    const markAsRead = useCallback(async (id) => {
        // Optimistic update
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
        );
        // Call API in background
        try {
            await apiMarkAsRead(id);
        } catch (e) {
            console.warn("Failed to mark notification as read");
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        // Optimistic update
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        // Call API in background
        try {
            await apiMarkAllAsRead();
        } catch (e) {
            console.warn("Failed to mark all notifications as read");
        }
    }, []);

    // Enhanced setUser that also updates isLoggedIn state
    const updateUser = useCallback((userData) => {
        const normalizedUser = {
            ...userData,
            // FIX Issue 4: Ensure hasLoadedOnce is always mapped
            hasLoadedOnce:
                userData.hasLoadedOnce || userData.has_loaded_once || false,
            // Phone verification status
            phoneVerified:
                userData.phoneVerified || userData.phone_verified || false,
            phone_verified:
                userData.phoneVerified || userData.phone_verified || false,
        };
        cachedUserRef.current = normalizedUser;
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        if (userData && userData.id) {
            setIsLoggedIn(true);
            setAuthStatus("ready");
        }
    }, []);

    // Login function to properly set user and logged in state
    const loginUser = useCallback(
        (userData) => {
            const normalizedUser = {
                ...defaultUser,
                ...userData,
                usernameEditable: userData.usernameEditable !== false,
                // FIX Issue 4: Explicitly map hasLoadedOnce for first-load bonus display
                hasLoadedOnce:
                    userData.hasLoadedOnce || userData.has_loaded_once || false,
                // Phone verification status
                phoneVerified:
                    userData.phoneVerified || userData.phone_verified || false,
                phone_verified:
                    userData.phoneVerified || userData.phone_verified || false,
            };
            cachedUserRef.current = normalizedUser;
            localStorage.setItem("user", JSON.stringify(normalizedUser));
            setUser(normalizedUser);
            setIsLoggedIn(true);
            setAuthStatus("ready");
            // Check for pending deposits after login
            loadPendingGatewayDeposit();
        },
        [loadPendingGatewayDeposit],
    );

    // Refresh user from server and update context state
    const refreshUser = useCallback(async () => {
        try {
            const sessionId = localStorage.getItem("sessionId");
            if (!sessionId) return;
            const apiBase = process.env.REACT_APP_BACKEND_URL || "";
            const response = await fetch(`${apiBase}/api/auth/me`, {
                headers: { Authorization: `Bearer ${sessionId}` },
            });
            if (response.ok) {
                const userData = await response.json();
                updateUser(userData);
            } else if (response.status === 401 || response.status === 403) {
                localStorage.removeItem("sessionId");
                localStorage.removeItem("user");
                cachedUserRef.current = null;
                setUser(defaultUser);
                setIsLoggedIn(false);
                setAuthStatus("signed_out");
            }
        } catch (e) {
            setAuthStatus("reconnecting");
            console.warn("AppContext refreshUser network error:", e);
        }
    }, [updateUser]);

    return (
        <AppContext.Provider
            value={{
                user,
                setUser: updateUser,
                refreshUser,
                loginUser,
                wallet,
                setWallet,
                refreshWallet,
                notifications,
                setNotifications,
                refreshNotifications,
                unreadCount,
                markAsRead,
                markAllAsRead,
                logout,
                loading,
                isLoggedIn,
                setIsLoggedIn,
                authStatus,
                // Level-native shared progression state
                levelData,
                refreshLevel,
                // Gateway deposit background polling
                pendingGatewayDeposit,
                savePendingGatewayDeposit,
                clearPendingGatewayDeposit,
                getPollCount: () => pollCountRef.current,
                triggerManualPoll: async () => {
                    // Cancel any scheduled poll
                    if (backgroundPollerRef.current) {
                        clearTimeout(backgroundPollerRef.current);
                        backgroundPollerRef.current = null;
                    }
                    // Trigger immediate poll by re-running the effect
                    // This is done by setting a flag that will be picked up
                    return true;
                },
            }}
        >
            {children}
            {/* Show announcement dialog after login */}
            <AnnouncementDialog isLoggedIn={isLoggedIn} />
        </AppContext.Provider>
    );
}

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useApp must be used within AppProvider");
    }
    return context;
}

export default AppProvider;
